import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import HeaderVariant from '../common/HeaderVariant';
import Footer from '../Footer/Footer';

const SuccessAnimation = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
  >
    <motion.div
      initial={{ scale: 0.5 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0.5 }}
      className="bg-white text-black p-8 rounded-lg shadow-xl text-center"
    >
      <div className="w-16 h-16 bg-green-500 rounded-full mx-auto mb-4 flex items-center justify-center">
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="Success checkmark">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="text-2xl font-bold mb-2">Application Received!</h3>
      <p className="text-gray-600">Redirecting you to schedule an interview...</p>
    </motion.div>
  </motion.div>
);

const CareerForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    email: '',
    position: '',
    cv: null,
    heardAboutUsSource: '',
    heardAboutUsDetail: '',
    socialMediaPlatform: '',
    disclaimer: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setFormData({ ...formData, cv: file });
    } else {
      alert('Please select a PDF file');
    }
  };

  const handleSourceChange = (e) => {
    const source = e.target.value;
    setFormData({
      ...formData,
      heardAboutUsSource: source,
      heardAboutUsDetail: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare the data for Google Sheets
      const sheetData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        position: formData.position,
        heardAboutUsSource: formData.heardAboutUsSource,
        socialMediaPlatform: formData.socialMediaPlatform,
        heardAboutUsDetail: formData.heardAboutUsDetail
      };

      // Send data to Google Sheets
      const response = await fetch('https://script.google.com/macros/s/AKfycbyiGF5Z4T3ZQWcHrXASP6D5liTxd74auGWLm7WVdf8ImUnN2eTGuGBeTpul05Cle0wq/exec', {
        redirect: 'follow',
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          data: sheetData
        })
      });

      // Send email with CV attachment
      const emailFormData = new FormData();
      emailFormData.append('_subject', 'New Career Application');
      emailFormData.append('_template', 'table');
      emailFormData.append('_captcha', 'false');
      emailFormData.append('_replyto', 'concierge@jacks.com.co');
      emailFormData.append('_from', 'concierge@jacks.com.co');
      
      // Add all form fields except CV
      for (const [key, value] of Object.entries(sheetData)) {
        emailFormData.append(key, value);
      }
      
      // Add CV if present
      if (formData.cv) {
        emailFormData.append('cv', formData.cv, formData.cv.name);
      }

      const emailResponse = await fetch('https://formsubmit.co/concierge@jacks.com.co', {
        method: 'POST',
        body: emailFormData
      });

      if (!emailResponse.ok) {
        throw new Error('Email submission failed');
      }

      setSubmitStatus('success');
      setShowSuccess(true);
      
      setTimeout(() => {
        setShowSuccess(false);
        window.location.href = 'https://calendly.com/hello-jacks/jacks-sales';
      }, 3000);
      
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        address: '',
        email: '',
        position: '',
        cv: null,
        heardAboutUsSource: '',
        heardAboutUsDetail: '',
        socialMediaPlatform: '',
        disclaimer: ''
      });
      
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    }

    setIsSubmitting(false);
  };

  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const inputClassName = "w-full bg-[#1A1A1A] text-white px-4 py-3 rounded-md border border-gray-800 focus:border-white focus:ring-1 focus:ring-white transition-colors";

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                  className={inputClassName}
                  placeholder="John"
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  required
                  className={inputClassName}
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className={inputClassName}
                placeholder="(123) 456-7890"
              />
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-300 mb-2">
                Address
              </label>
              <input
                type="text"
                name="address"
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
                className={inputClassName}
                placeholder="123 Main Street, City, State"
              />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={nextStep}
                disabled={!formData.firstName || !formData.lastName || !formData.phone || !formData.address}
                className={`px-8 py-3 bg-white text-black rounded-md transition-all font-medium
                  ${(!formData.firstName || !formData.lastName || !formData.phone || !formData.address) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 hover:scale-[0.99]'}
                `}
              >
                Next
              </button>
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className={inputClassName}
                placeholder="john.doe@example.com"
              />
            </div>

            <div>
              <label htmlFor="position" className="block text-sm font-medium text-gray-300 mb-2">
                Position
              </label>
              <select
                name="position"
                id="position"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                required
                className={inputClassName}
              >
                <option value="">Select a position</option>
                <option value="Sales">Sales</option>
                <option value="Office Administrator">Office Administrator</option>
              </select>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={prevStep}
                className="px-8 py-3 border border-white text-white rounded-md transition-all font-medium hover:bg-white/10"
              >
                Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                disabled={!formData.email || !formData.position}
                className={`px-8 py-3 bg-white text-black rounded-md transition-all font-medium
                  ${(!formData.email || !formData.position) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 hover:scale-[0.99]'}
                `}
              >
                Next
              </button>
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div>
              <label htmlFor="cv" className="block text-sm font-medium text-gray-300 mb-2">
                Resume (PDF)
              </label>
              <div className="relative">
                <input
                  type="file"
                  name="cv"
                  id="cv"
                  accept=".pdf"
                  onChange={handleFileChange}
                  required
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="w-full bg-black text-white px-4 py-3 rounded-md border border-gray-800 focus-within:border-white transition-colors flex items-center">
                  <span className="bg-white text-black px-4 py-2 rounded-full text-sm font-semibold mr-4">
                    Choose File
                  </span>
                  <span className="text-gray-400">
                    {formData.cv ? formData.cv.name : 'No file selected'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={prevStep}
                className="px-8 py-3 border border-white text-white rounded-md transition-all font-medium hover:bg-white/10"
              >
                Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                disabled={!formData.cv}
                className={`px-8 py-3 bg-white text-black rounded-md transition-all font-medium
                  ${!formData.cv ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 hover:scale-[0.99]'}
                `}
              >
                Next
              </button>
            </div>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="space-y-4">
              <div>
                <label htmlFor="heardAboutUsSource" className="block text-sm font-medium text-gray-300 mb-2">
                  How did you hear about us?
                </label>
                <select
                  name="heardAboutUsSource"
                  id="heardAboutUsSource"
                  value={formData.heardAboutUsSource}
                  onChange={handleSourceChange}
                  required
                  className={inputClassName}
                >
                  <option value="">Select an option</option>
                  <option value="socialMedia">Social Media</option>
                  <option value="friend">Friend</option>
                  <option value="event">Event</option>
                  <option value="indeed">Indeed</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {formData.heardAboutUsSource && formData.heardAboutUsSource !== 'indeed' && (
                <div>
                  <label htmlFor="heardAboutUsDetail" className="block text-sm font-medium text-gray-300 mb-2">
                    {formData.heardAboutUsSource === 'socialMedia' 
                      ? 'Which social media platform?' 
                      : formData.heardAboutUsSource === 'friend'
                      ? 'Friend\'s name'
                      : formData.heardAboutUsSource === 'event'
                      ? 'Event name'
                      : 'Please specify'}
                  </label>
                  {formData.heardAboutUsSource === 'socialMedia' ? (
                    <div className="space-y-4">
                      <select
                        name="socialMediaPlatform"
                        id="socialMediaPlatform"
                        value={formData.socialMediaPlatform || ''}
                        onChange={(e) => setFormData({ ...formData, socialMediaPlatform: e.target.value, heardAboutUsDetail: '' })}
                        required
                        className={inputClassName}
                      >
                        <option value="">Select platform</option>
                        <option value="Instagram">Instagram</option>
                        <option value="Facebook">Facebook</option>
                        <option value="LinkedIn">LinkedIn</option>
                        <option value="TikTok">TikTok</option>
                      </select>
                      {formData.socialMediaPlatform && (
                        <div>
                          <label htmlFor="heardAboutUsDetail" className="block text-sm font-medium text-gray-300 mb-2">
                            Enter {formData.socialMediaPlatform} username
                          </label>
                          <input
                            type="text"
                            name="heardAboutUsDetail"
                            id="heardAboutUsDetail"
                            value={formData.heardAboutUsDetail}
                            onChange={(e) => setFormData({ ...formData, heardAboutUsDetail: e.target.value })}
                            required
                            className={inputClassName}
                            placeholder={`@username on ${formData.socialMediaPlatform}`}
                          />
                        </div>
                      )}
                    </div>
                  ) : formData.heardAboutUsSource === 'event' ? (
                    <input
                      type="text"
                      name="heardAboutUsDetail"
                      id="heardAboutUsDetail"
                      value={formData.heardAboutUsDetail}
                      onChange={(e) => setFormData({ ...formData, heardAboutUsDetail: e.target.value })}
                      required
                      className={inputClassName}
                      placeholder="Event name"
                    />
                  ) : (
                    <input
                      type="text"
                      name="heardAboutUsDetail"
                      id="heardAboutUsDetail"
                      value={formData.heardAboutUsDetail}
                      onChange={(e) => setFormData({ ...formData, heardAboutUsDetail: e.target.value })}
                      required
                      className={inputClassName}
                      placeholder={formData.heardAboutUsSource === 'friend'
                        ? 'Friend\'s full name'
                        : 'Please tell us how you heard about us'}
                    />
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-gray-800 pt-6">
              <div className="mb-6">
                <p className="text-gray-300 text-sm">
                  After submitting your application, please wait to be redirected to our calendar where you can schedule your interview.
                </p>
              </div>
              <div className="flex items-start mb-6">
                <div className="flex items-center h-5">
                  <input
                    id="disclaimer"
                    name="disclaimer"
                    type="checkbox"
                    required
                    checked={formData.disclaimer === "I Agree"}
                    onChange={(e) => setFormData({
                      ...formData, 
                      disclaimer: e.target.checked ? "I Agree" : ""
                    })}
                    className="w-4 h-4 border border-gray-800 rounded bg-[#1A1A1A] checked:bg-white checked:border-white focus:ring-1 focus:ring-white text-black transition-colors cursor-pointer"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="disclaimer" className="font-light text-gray-300">
                    I agree to the{' '}
                    <a href="/terms-of-use" className="font-medium text-blue-500 hover:underline">
                      Terms and Conditions
                    </a>{', '}
                    <a href="/disclaimer" className="font-medium text-blue-500 hover:underline">
                      Disclaimer
                    </a>{', '}
                    and acknowledge that my information will be used in accordance with the{' '}
                    <a href="/privacypolicy" className="font-medium text-blue-500 hover:underline">
                      Privacy Policy
                    </a>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={prevStep}
                className="px-8 py-3 border border-white text-white rounded-md transition-all font-medium hover:bg-white/10"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !formData.heardAboutUsSource || (!formData.heardAboutUsDetail && formData.heardAboutUsSource !== 'indeed') || !formData.disclaimer}
                className={`px-8 py-3 bg-white text-black rounded-md transition-all font-medium
                  ${(isSubmitting || !formData.heardAboutUsSource || (!formData.heardAboutUsDetail && formData.heardAboutUsSource !== 'indeed') || !formData.disclaimer) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 hover:scale-[0.99]'}
                `}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <HeaderVariant />
      
      <div className="relative min-h-screen bg-black">
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.02] select-none pointer-events-none overflow-hidden">
          <h1 className="text-[20vw] font-bold text-white whitespace-nowrap">CAREER</h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 container mx-auto px-4 py-24 pt-32"
        >
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16 pt-24">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Join Our Team
              </h1>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Be part of a dynamic team that's revolutionizing personal transportation. At Jacks, we're always looking for talented individuals to help shape the future of mobility.
              </p>
            </div>

            <div className="bg-[#111111] border border-gray-800 rounded-2xl p-8">
              <div className="flex justify-between mb-8">
                {[1, 2, 3, 4].map((step) => (
                  <div
                    key={step}
                    className={`flex items-center ${step < 4 ? 'flex-1' : ''}`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors
                        ${currentStep >= step ? 'bg-white text-black' : 'bg-gray-800 text-gray-400'}
                      `}
                    >
                      {step}
                    </div>
                    {step < 4 && (
                      <div
                        className={`flex-1 h-px mx-2 transition-colors
                          ${currentStep > step ? 'bg-white' : 'bg-gray-800'}
                        `}
                      />
                    )}
                  </div>
                ))}
              </div>

              <form onSubmit={handleSubmit} className="space-y-8" encType="multipart/form-data">
                <input type="hidden" name="_subject" value="New Career Application" />
                <input type="hidden" name="_template" value="table" />
                <input type="hidden" name="_captcha" value="false" />

                <AnimatePresence mode="wait">
                  {renderStep()}
                </AnimatePresence>

                {submitStatus === 'success' && (
                  <div className="text-green-500 text-center bg-green-500/10 p-4 rounded-md">
                    Application submitted successfully! We will contact you soon.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="text-red-500 text-center bg-red-500/10 p-4 rounded-md">
                    An error occurred. Please try again.
                  </div>
                )}
              </form>
            </div>
          </div>
        </motion.div>
      </div>

      <Footer />

      <AnimatePresence>
        {showSuccess && <SuccessAnimation />}
      </AnimatePresence>
    </div>
  );
};

export default CareerForm; 